import {TimeAgoText} from '@/components/general'
import {MemberAvatar} from '@/components/members'
import {TableCell, TableRow, TableRowMenuButton} from '@/components/table'
import {Studio, TableRowAction} from '@/types/index'
import {truncateString} from '@/utils/general'
import {RemoveCircleIcon, EyeClosedIcon, EyeOpenIcon} from '@sanity/icons'
import {Badge, Text, useToast} from '@sanity/ui'
import Link from 'next/link'
import {RemoveStudioDialog} from './RemoveStudioDialog'
import {useConfirmDialog} from '@/components/dialog'
import {useCallback} from 'react'
import {useStudios} from '@/data/studios/useStudios'
import {useCurrentScopeContext} from '@/context/useCurrentScopeContext'
import {Tooltip} from '@/ui/ui-components/primitives/tooltip/Tooltip'
import {VersionBadge} from './versionBadge'
import {getStudioFullUrl, getStudioHostname} from '@/utils/studioHost'

interface Props {
  projectId: string
  studio: Studio
  isLast: boolean
}

const studioUrl = (studio: Studio) => {
  const url = getStudioFullUrl(studio)
  const cleanUrl = getStudioHostname(studio)
  const urlToDisplay = truncateString(cleanUrl, 30)
  const linkElement = (
    <Link href={`${url}`} target="_blank" rel="noreferrer noopener">
      <Text weight="semibold" size={[1, 1, 1, 2]} textOverflow="ellipsis">
        {urlToDisplay}
      </Text>
    </Link>
  )
  // if truncating, show tooltip with full url
  if (urlToDisplay !== cleanUrl) {
    return (
      <Tooltip
        content={
          <Text size={1} muted>
            {cleanUrl}
          </Text>
        }
        portal
        placement="top"
      >
        {linkElement}
      </Tooltip>
    )
  }

  return linkElement
}

const hostingBadge = (urlType: Studio['urlType']) => {
  if (urlType === 'internal') {
    return (
      <Badge padding={2} tone={'positive'}>
        Sanity
      </Badge>
    )
  }
  return (
    <Badge padding={2} tone={'caution'}>
      Other
    </Badge>
  )
}

const deployedBy = (userId?: string | null) => {
  if (userId) {
    return <MemberAvatar key={`member-${userId}`} userId={userId} size={1} tooltip />
  }
  return null
}

export function StudioItem({projectId, studio, isLast}: Props) {
  const {
    showDialog: showRemoveDialog,
    hideDialog: hideRemoveDialog,
    Dialog: RemoveDialog,
  } = useConfirmDialog()
  const toast = useToast()
  const {updateStudioDashboardStatusMutation} = useStudios(projectId)
  const {org} = useCurrentScopeContext()

  const studioDashboardEnabled = studio.dashboardStatus === 'default'

  const handleRemoveStudio = useCallback(() => {
    showRemoveDialog()
  }, [showRemoveDialog])

  const handleToggleDashboardVisibility = () => {
    const newStatus = studioDashboardEnabled ? 'disabled' : 'default'

    updateStudioDashboardStatusMutation.mutate(
      {
        studioId: studio.id,
        dashboardStatus: newStatus,
      },
      {
        onSuccess: () => {
          if (newStatus === 'disabled') {
            toast.push({
              status: 'success',
              title: 'Studio is hidden in Dashboard',
            })
          } else {
            toast.push({
              status: 'success',
              title: 'Studio is shown in Dashboard!',
            })
          }
        },
        onError: (error) => {
          toast.push({
            status: 'error',
            title: 'Something went wrong. Please try again.',
            description: error instanceof Error ? error.message : undefined,
          })
        },
      }
    )
  }

  const rowActions: TableRowAction[] = []

  if (org?.dashboardStatus === 'enabled') {
    rowActions.push({
      title: studioDashboardEnabled ? 'Hide in Dashboard' : 'Show in Dashboard',
      icon: studioDashboardEnabled ? EyeOpenIcon : EyeClosedIcon,
      tone: 'default',
      permissions: [{permissionName: 'sanity.project', grantName: 'update'}],
      mode: 'bleed',
      onAction: handleToggleDashboardVisibility,
    })
  }

  rowActions.push({
    title: 'Remove studio',
    icon: RemoveCircleIcon,
    tone: 'critical',
    permissions: [{permissionName: 'sanity.project', grantName: 'update'}],
    mode: 'bleed',
    onAction: handleRemoveStudio,
    dialog: (
      <RemoveStudioDialog
        key="remove-studio-dialog"
        Dialog={RemoveDialog}
        projectId={projectId}
        hideDialog={hideRemoveDialog}
        studio={studio}
      />
    ),
  })

  return (
    <>
      {rowActions.map((action) => typeof action !== 'string' && action.dialog)}
      <TableRow paddingY={4} paddingX={2} borderBottom={!isLast}>
        <TableCell>{studioUrl(studio)}</TableCell>
        <TableCell>
          <Text>
            <TimeAgoText
              size={1}
              date={studio.activeDeployment?.deployedAt || studio.updatedAt}
              muted
              options={{agoSuffix: true}}
            />
          </Text>
        </TableCell>
        <TableCell $align={'center'}>{hostingBadge(studio.urlType)}</TableCell>
        <TableCell $align={'center'}>
          <VersionBadge studio={studio} projectId={projectId} />
        </TableCell>
        <TableCell $align={'center'}>{deployedBy(studio.activeDeployment?.deployedBy)}</TableCell>
        <TableCell $align={'right'}>
          <TableRowMenuButton
            menuId={studio.id}
            label={`Studio actions for ${studio.id}`}
            rowItem={studio}
            actions={rowActions}
            roleScope="project"
          />
        </TableCell>
      </TableRow>
    </>
  )
}
